import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CFSelect from "../shared/forms/CFSelect";
import CalculateValueService from "packs/services/convertflow/actions/calculate_value_service";
import { VariantContextData } from "../variants/VariantBuilderContext";
import apiRequest from "../../utils/apiRequest";

const FORMULA_OPERATORS = [
  { label: "+", insert: " + ", title: "Add" },
  { label: "-", insert: " - ", title: "Subtract" },
  { label: "*", insert: " * ", title: "Multiply" },
  { label: "/", insert: " / ", title: "Divide" },
  { label: "%", insert: " % ", title: "Modulo (remainder)" },
  { label: "(", insert: "(", title: "Open parenthesis" },
  { label: ")", insert: ")", title: "Close parenthesis" },
  { label: ">", insert: " > ", title: "Greater than" },
  { label: ">=", insert: " >= ", title: "Greater than or equal to" },
  { label: "<", insert: " < ", title: "Less than" },
  { label: "<=", insert: " <= ", title: "Less than or equal to" },
  { label: "==", insert: " == ", title: "Equal to" },
  { label: "!=", insert: " != ", title: "Not equal to" },
  {
    label: "and",
    insert: " and ",
    title: "Logical AND — both conditions must be true",
  },
  {
    label: "or",
    insert: " or ",
    title: "Logical OR — either condition must be true",
  },
  {
    label: "not",
    insert: "not ",
    title: "Logical NOT — negates the condition",
  },
];

const FORMULA_FUNCTIONS = [
  {
    label: "if()",
    insert: "if(, , )",
    tooltip: "if(condition, then, else)",
    cursorOffset: -5,
  },
  {
    label: "round()",
    insert: "round(, )",
    tooltip: "round(value, decimals)",
    cursorOffset: -3,
  },
  {
    label: "roundup()",
    insert: "roundup(, )",
    tooltip: "roundup(value, decimals)",
    cursorOffset: -3,
  },
  {
    label: "rounddown()",
    insert: "rounddown(, )",
    tooltip: "rounddown(value, decimals)",
    cursorOffset: -3,
  },
  {
    label: "min()",
    insert: "min(, )",
    tooltip: "min(a, b)",
    cursorOffset: -3,
  },
  {
    label: "max()",
    insert: "max(, )",
    tooltip: "max(a, b)",
    cursorOffset: -3,
  },
];

const SELECT_MENU_PORTAL_Z_INDEX = 2147483002;

const selectMenuPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: SELECT_MENU_PORTAL_Z_INDEX }),
};

const AUTO_SAVE_DEBOUNCE_MS = 500;

const FormulaBuilderModal = ({
  formula,
  targetField,
  roundTo,
  website,
  onSave,
}) => {
  const [contextData, , setContextData] = useContext(VariantContextData) || [];

  const [formulaDraft, setFormulaDraft] = useState(formula || "");
  const [targetFieldSelection, setTargetFieldSelection] = useState(null);
  const [isCreatingNewField, setIsCreatingNewField] = useState(false);
  const [newFieldSlug, setNewFieldSlug] = useState("");
  const [roundToDraft, setRoundToDraft] = useState("");
  const [sampleValues, setSampleValues] = useState({});
  const [showFieldPicker, setShowFieldPicker] = useState(false);

  const formulaInputRef = useRef(null);
  const fieldPickerRef = useRef(null);
  const lastPersistedRef = useRef(null);
  const initializedRef = useRef(false);
  const pendingPersistRef = useRef(null);
  const onSaveRef = useRef(onSave);

  const liveFieldsSignature = useMemo(() => {
    const wf = Object.values(contextData?.objects?.website?.fields || {});
    const vf = Object.values(contextData?.objects?.variant?.fields || {});
    const integs = Object.values(
      contextData?.objects?.website?.integrations || {}
    );
    return [
      ...wf.map((f) => `${f?.id}:${f?.slug}:${f?.service || ""}`),
      ...vf.map((f) => `${f?.id}:${f?.slug}:${f?.service || ""}`),
      ...integs.map((i) => `${i?.id}:${i?.integration_type}`),
    ]
      .sort()
      .join("|");
  }, [contextData]);

  const formulaFieldOptions = useMemo(() => {
    const liveWebsite = contextData?.objects?.website || website;
    if (!liveWebsite) return [];

    const allFields = {
      ...Object.values(liveWebsite.fields || {}).reduce((acc, f) => {
        if (f?.slug) acc[f.slug] = f;
        return acc;
      }, {}),
      ...Object.values(contextData?.objects?.variant?.fields || {}).reduce(
        (acc, f) => {
          if (f?.slug) acc[f.slug] = f;
          return acc;
        },
        {}
      ),
    };

    const integrations = Object.values(liveWebsite.integrations || {})
      .filter(
        (integration) =>
          integration.features && integration.features.includes("contact")
      )
      .sort((a, b) =>
        (a.integration_type || "").localeCompare(b.integration_type || "")
      );

    const integrationByType = {};
    integrations.forEach((integration) => {
      if (integration.integration_type) {
        integrationByType[integration.integration_type] = integration;
      }
    });

    const integrationIcon = (integration) => {
      if (!(integration && integration.icon)) return undefined;
      return `/images/integrations/${integration.icon.split("/").pop()}`;
    };

    const convertFlowGroup = {
      label: "Saved Custom Fields",
      options: [],
    };
    const convertFlowSeen = new Set();

    const integrationGroups = {};
    integrations.forEach((integration) => {
      integrationGroups[integration.integration_type] = {
        label: integration.integration_type,
        options: [],
      };
    });

    const integrationSeen = {};
    const addIntegrationOption = (integrationType, option) => {
      if (!integrationGroups[integrationType]) return;
      integrationSeen[integrationType] =
        integrationSeen[integrationType] || new Set();

      if (!integrationSeen[integrationType].has(option.value)) {
        integrationSeen[integrationType].add(option.value);
        integrationGroups[integrationType].options.push(option);
      }
    };

    Object.values(allFields).forEach((field) => {
      if (!(field && field.slug) || field.slug === "New Field") return;

      const service = field.service;
      const matchingIntegration = service ? integrationByType[service] : null;

      if (service && service !== "ConvertFlow" && !matchingIntegration) return;
      if (!service || service === "ConvertFlow") {
        if (!convertFlowSeen.has(field.slug)) {
          convertFlowSeen.add(field.slug);
          convertFlowGroup.options.push({
            value: field.slug,
            label: field.slug,
            icon: "/images/convertflow-icon-no-bg.svg",
          });
        }
      } else {
        addIntegrationOption(service, {
          value: field.slug,
          label: field.slug,
          icon: integrationIcon(matchingIntegration),
        });
      }
    });

    integrations.forEach((integration) => {
      if (integration.api_data && integration.api_data.properties) {
        Object.values(integration.api_data.properties).forEach((property) => {
          if (!(property && property.id)) return;

          addIntegrationOption(integration.integration_type, {
            value: property.id,
            label: property.name || property.id,
            icon: integrationIcon(integration),
          });
        });
      }
    });

    convertFlowGroup.options.sort((a, b) => a.label.localeCompare(b.label));
    Object.values(integrationGroups).forEach((group) => {
      group.options.sort((a, b) => a.label.localeCompare(b.label));
    });

    const groups = [];
    if (convertFlowGroup.options.length > 0) {
      groups.push(convertFlowGroup);
    }

    Object.values(integrationGroups).forEach((group) => {
      if (group.options.length > 0) {
        groups.push(group);
      }
    });

    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [website, liveFieldsSignature]);

  const resultFieldOptions = useMemo(() => {
    const groups = JSON.parse(JSON.stringify(formulaFieldOptions || []));

    let savedFieldsGroup = groups.find(
      (group) => group.label === "Saved Custom Fields"
    );

    if (!savedFieldsGroup) {
      savedFieldsGroup = { label: "Saved Custom Fields", options: [] };
      groups.unshift(savedFieldsGroup);
    }

    savedFieldsGroup.options.push({
      label: "New custom field",
      value: "New Field",
      icon: contextData?.dark_mode
        ? "/images/add-icon-white.svg"
        : "/images/add-icon.svg",
    });

    return groups;
  }, [formulaFieldOptions, contextData?.dark_mode]);

  const findOptionInGroups = useCallback((groups, value) => {
    if (!value) return null;

    for (
      let groupIndex = 0;
      groupIndex < (groups || []).length;
      groupIndex += 1
    ) {
      const group = groups[groupIndex];
      const match = (group.options || []).find(
        (option) => option.value === value
      );

      if (match) {
        return match;
      }
    }

    return null;
  }, []);

  const findResultFieldOption = useCallback(
    (value) => {
      if (!value) return null;
      return (
        findOptionInGroups(resultFieldOptions, value) || {
          value,
          label: value,
          icon: "/images/convertflow-icon-no-bg.svg",
        }
      );
    },
    [resultFieldOptions, findOptionInGroups]
  );

  const initializeDraft = useCallback(() => {
    initializedRef.current = false;
    const normalizedFormula = formula || "";
    const normalizedField = targetField || "";
    const normalizedRoundTo =
      roundTo === undefined || roundTo === null ? "" : `${roundTo}`.trim();

    setFormulaDraft(normalizedFormula);
    setTargetFieldSelection(findResultFieldOption(normalizedField));
    setIsCreatingNewField(false);
    setNewFieldSlug("");
    setRoundToDraft(normalizedRoundTo);
    setSampleValues({});
    setShowFieldPicker(false);

    lastPersistedRef.current = `${normalizedFormula}__${normalizedField}__${normalizedRoundTo}`;
  }, [findResultFieldOption, formula, roundTo, targetField]);

  useEffect(() => {
    initializeDraft();
    // Seed the drafts from the incoming props once per mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showFieldPicker) return undefined;

    const handleOutsideClick = (event) => {
      if (
        fieldPickerRef.current &&
        !fieldPickerRef.current.contains(event.target)
      ) {
        setShowFieldPicker(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [showFieldPicker]);

  const insertAtCursor = useCallback(
    (text, cursorOffset = 0) => {
      const input = formulaInputRef.current;
      const currentValue = formulaDraft || "";

      const start =
        input && typeof input.selectionStart === "number"
          ? input.selectionStart
          : currentValue.length;
      const end =
        input && typeof input.selectionEnd === "number"
          ? input.selectionEnd
          : currentValue.length;

      const nextValue =
        currentValue.substring(0, start) + text + currentValue.substring(end);

      setFormulaDraft(nextValue);

      window.requestAnimationFrame(() => {
        if (!input) return;
        input.focus();

        const position = Math.max(
          0,
          Math.min(nextValue.length, start + text.length + cursorOffset)
        );

        input.setSelectionRange(position, position);
      });
    },
    [formulaDraft]
  );

  const formulaFieldReferences = useMemo(() => {
    return CalculateValueService.fieldReferences(formulaDraft);
  }, [formulaDraft]);

  useEffect(() => {
    setSampleValues((previousValues) => {
      const nextValues = {};
      formulaFieldReferences.forEach((reference) => {
        nextValues[reference] =
          previousValues[reference] === undefined
            ? "0"
            : previousValues[reference];
      });
      return nextValues;
    });
  }, [formulaFieldReferences]);

  const sampleEvaluation = useMemo(
    () =>
      CalculateValueService.evaluateFormula(formulaDraft, sampleValues, {
        roundTo: roundToDraft,
      }),
    [formulaDraft, roundToDraft, sampleValues]
  );

  const persistPayload = useMemo(
    () => ({
      formula: formulaDraft,
      field: targetFieldSelection ? targetFieldSelection.value : "",
      roundTo: roundToDraft,
    }),
    [formulaDraft, roundToDraft, targetFieldSelection]
  );

  const persistKey = useMemo(
    () =>
      `${persistPayload.formula}__${persistPayload.field}__${persistPayload.roundTo}`,
    [persistPayload]
  );

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    if (!onSave) return undefined;

    // Suppress auto-save until the draft state from initializeDraft has landed.
    // On the initial render the draft state is still its useState seed (e.g. an
    // empty target field), so persistKey would differ from the initialized
    // baseline and fire a spurious onSave that clobbers the incoming value.
    if (!initializedRef.current) {
      if (lastPersistedRef.current === persistKey) {
        initializedRef.current = true;
      }
      return undefined;
    }

    if (lastPersistedRef.current === persistKey) {
      pendingPersistRef.current = null;
      return undefined;
    }

    // Debounce: persisting per keystroke would flood the builder's undo
    // history (one updateBuilder dispatch per character) and, outside the
    // builder, fire an AJAX update per character.
    pendingPersistRef.current = { key: persistKey, payload: persistPayload };
    const timer = setTimeout(() => {
      pendingPersistRef.current = null;
      lastPersistedRef.current = persistKey;
      onSaveRef.current(persistPayload);
    }, AUTO_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [onSave, persistKey, persistPayload]);

  // Flush a pending debounced save if the component unmounts mid-typing
  // (e.g. the slide-out panel closes), so the last keystrokes aren't lost.
  useEffect(
    () => () => {
      const pending = pendingPersistRef.current;
      if (
        pending &&
        onSaveRef.current &&
        pending.key !== lastPersistedRef.current
      ) {
        lastPersistedRef.current = pending.key;
        onSaveRef.current(pending.payload);
      }
    },
    []
  );

  if (typeof document === "undefined") {
    return null;
  }

  const tokenMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const body = (
    <div>
      <div className="rounded-xl px-5 border border-solid border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
        <div className="my-6 space-y-2">
          <div className="text-xl font-semibold text-slate-700">Formula</div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <input
              ref={formulaInputRef}
              type="text"
              autoComplete="off"
              spellCheck={false}
              aria-label="Formula"
              className="formula-builder-interactive formula-builder-input-guard w-full h-[40px] px-3 rounded-xl border border-solid border-slate-200 bg-white text-xl font-mono"
              placeholder="e.g. {dog_weight} * 0.15"
              value={formulaDraft}
              onChange={(event) => setFormulaDraft(event.target.value)}
              onMouseDown={(event) => event.stopPropagation()}
            />

            <div className="relative sm:shrink-0" ref={fieldPickerRef}>
              <button
                type="button"
                className="h-[40px] px-3 rounded-xl border border-solid border-slate-200 bg-white text-xl text-slate-700 cursor-pointer"
                onMouseDown={tokenMouseDown}
                onClick={() => setShowFieldPicker((previous) => !previous)}
                aria-label="Open field picker"
                aria-expanded={showFieldPicker}
                aria-haspopup="listbox"
              >
                + Field
              </button>

              {showFieldPicker && (
                <div className="absolute p-4 bg-white rounded-xl shadow-sm right-0 top-full mt-1 w-[260px] z-[2147483002]">
                  <span className="field-title">Select a field</span>
                  <CFSelect
                    className="smart-select bg-white border-solid border-[1px] border-slate-200 rounded-md formula-builder-interactive"
                    options={formulaFieldOptions}
                    placeholder="Select a field"
                    isSearchable={true}
                    menuPortalTarget={document.body}
                    styles={selectMenuPortalStyles}
                    value={null}
                    onChange={(selected) => {
                      if (selected && selected.value) {
                        insertAtCursor(`{${selected.value}}`);
                      }

                      setShowFieldPicker(false);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <p className="m-0 text-xl text-slate-500">
            use <code className="font-mono">{`{field_slug}`}</code> to reference
            a field. Example:{" "}
            <code className="font-mono">{`{dog_weight} * 0.15`}</code>
          </p>
        </div>

        <div className="mb-10 flex items-start gap-3">
          <div className="w-[95px] shrink-0 pt-1 text-xl font-semibold text-slate-700">
            Operators
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FORMULA_OPERATORS.map((operator) => (
              <button
                key={`operator-${operator.label}`}
                type="button"
                className="formula-operator-btn inline-flex items-center rounded-lg border border-solid border-slate-200 bg-white px-2 py-1 text-xl font-mono text-slate-700 cursor-pointer"
                data-tooltip={operator.title}
                aria-label={`Insert operator ${operator.label}`}
                onMouseDown={tokenMouseDown}
                onClick={() => insertAtCursor(operator.insert)}
              >
                {operator.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-10 flex items-start gap-3">
          <div className="w-[95px] shrink-0 pt-1 text-xl font-semibold text-slate-700">
            Functions
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {FORMULA_FUNCTIONS.map((formulaFunction) => (
              <button
                key={`function-${formulaFunction.label}`}
                type="button"
                className="formula-operator-btn inline-flex items-center rounded-lg border border-solid border-slate-200 bg-white px-2 py-1 text-xl text-slate-700 cursor-pointer"
                data-tooltip={formulaFunction.tooltip}
                aria-label={`Insert function ${formulaFunction.label}`}
                onMouseDown={tokenMouseDown}
                onClick={() =>
                  insertAtCursor(
                    formulaFunction.insert,
                    formulaFunction.cursorOffset || 0
                  )
                }
              >
                {formulaFunction.label}
              </button>
            ))}
          </div>
        </div>

        <div className="my-10 pt-3 border-0 border-t border-solid border-slate-200 dark:border-slate-700">
          <div className="my-5 text-xl font-semibold text-slate-700">
            Try it out!
          </div>

          <div className="">
            <div>
              {formulaFieldReferences.length === 0 ? (
                <div className="text-xl text-slate-500">
                  Add field references to test sample output.
                </div>
              ) : (
                <div className="grid mb-4 grid-cols-2 gap-2">
                  {formulaFieldReferences.map((reference) => (
                    <label key={reference} className="text-xs text-slate-600">
                      <span className="mb-1 font-mono text-xl">{`{${reference}}`}</span>
                      <input
                        type="number"
                        className="formula-builder-interactive formula-builder-input-guard w-full h-[32px] px-2 rounded-lg border border-solid border-slate-200 bg-white text-xl"
                        value={sampleValues[reference] || ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSampleValues((previousValues) => ({
                            ...previousValues,
                            [reference]: value,
                          }));
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-10 rounded-lg border border-solid border-slate-200 bg-white px-3 py-2">
              <div className="text-[11px] uppercase tracking-[0.04em] text-slate-500">
                Result:
              </div>
              <div
                className={`mt-1 text-xl ${
                  sampleEvaluation.success
                    ? "text-emerald-700"
                    : "text-rose-600"
                }`}
              >
                {sampleEvaluation.success
                  ? `${sampleEvaluation.result}`
                  : sampleEvaluation.error}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="my-10">
          <label className="mt-1 mb-[5px] inline-flex justify-between align-middle w-full gap-2">
            <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap flex-1">
              Save Value To Field
            </span>
          </label>
          <CFSelect
            className="formula-builder-interactive"
            options={resultFieldOptions}
            placeholder="Select a custom field"
            isSearchable={true}
            menuPortalTarget={document.body}
            styles={selectMenuPortalStyles}
            value={
              isCreatingNewField
                ? { label: "New custom field", value: "New Field" }
                : targetFieldSelection
            }
            onChange={(selected) => {
              if (selected && selected.value === "New Field") {
                setIsCreatingNewField(true);
                setNewFieldSlug("");
              } else {
                setIsCreatingNewField(false);
                setTargetFieldSelection(
                  selected && selected.value ? selected : null
                );
              }
            }}
          />
          {isCreatingNewField && (
            <div className="mt-2">
              <input
                type="text"
                className="formula-builder-interactive formula-builder-input-guard w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
                placeholder="e.g. customer_score"
                value={newFieldSlug}
                onChange={(event) => {
                  const slug = event.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, "_")
                    .replace(/_+/g, "_");
                  setNewFieldSlug(slug);
                }}
                onBlur={() => {
                  setIsCreatingNewField(false);
                  if (newFieldSlug) {
                    setTargetFieldSelection({
                      value: newFieldSlug,
                      label: newFieldSlug,
                    });
                    if (contextData && setContextData) {
                      apiRequest(
                        "POST",
                        "./fields/",
                        {
                          field: {
                            slug: newFieldSlug,
                            label: newFieldSlug,
                            field_type: "text",
                            service: "ConvertFlow",
                          },
                        },
                        (fieldData) => {
                          const slug =
                            fieldData &&
                            typeof fieldData.slug === "string" &&
                            fieldData.slug;
                          if (slug && !fieldData.error && !fieldData.errors) {
                            setContextData((prev) => {
                              let updated = { ...prev };
                              updated.objects.website.fields[slug] = fieldData;
                              return updated;
                            });
                          }
                        }
                      );
                    }
                  }
                }}
                onMouseDown={(event) => event.stopPropagation()}
              />
              {!newFieldSlug && (
                <p className="mt-1 text-sm text-rose-500">
                  Field data name is required
                </p>
              )}
            </div>
          )}
        </div>

        <div className="mb-10">
          <label className="mt-1 mb-[5px] inline-flex justify-between align-middle w-full gap-2">
            <span className="setting-label text-xl text-black dark:text-white capitalize whitespace-nowrap flex-1">
              Round Up To X Decimal Places
            </span>
          </label>
          <input
            type="number"
            min="0"
            step="1"
            className="formula-builder-interactive formula-builder-input-guard w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0 appearance-none"
            placeholder="2"
            value={roundToDraft}
            onChange={(event) => setRoundToDraft(event.target.value)}
            onMouseDown={(event) => event.stopPropagation()}
          />
        </div>
      </div>

    </div>
  );

  return <div className="formula-builder-inline w-full">{body}</div>;
};

export default FormulaBuilderModal;
